import React, { useEffect, useRef, useState, useCallback } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { GoogleMap, useJsApiLoader } from '@react-google-maps/api';
import { getGoogleMapsLoaderOptions } from '../config/googleMaps';
import { createTruckMarkerIcon } from '../utils/truckMarkerUtils';

interface AtRiskMove {
  pronumber: number;
  legnum: number;
  driver: string;
  origin_name: string;
  origin_city_state: string;
  dest_name: string;
  dest_city_state: string;
  status: string;
  risk_level: string;
  driver_lat: number | null;
  driver_lng: number | null;
  driver_heading: number | null;
  driver_speed: number | null;
  origin_coordinates: string | null;
  dest_coordinates: string | null;
  origin_geofence_radius: number;
  dest_geofence_radius: number;
  distance_to_dest_miles: number | null;
  appointment_time: string;
  deadline_date: string;
  deadline_time: string;
}

interface AtRiskMapDrawerProps {
  open: boolean;
  onClose: () => void;
  move: AtRiskMove | null;
}

// Stable identity — an inline literal would be a "new" center every render,
// making @react-google-maps/api call setCenter(Houston) and snap the map back
const DEFAULT_CENTER = { lat: 29.7604, lng: -95.3698 }; // Houston

const parseCoordinates = (coordStr: string | null): { lat: number; lng: number } | null => {
  if (!coordStr) return null;
  try {
    const parts = coordStr.split(',');
    // Handle both simple "lat,lng" and complex shapes with multiple points - just use first point
    if (parts.length >= 2) {
      const lat = parseFloat(parts[0].trim());
      const lng = parseFloat(parts[1].trim());
      if (!isNaN(lat) && !isNaN(lng) && lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180) {
        return { lat, lng };
      }
    }
  } catch {
    // ignore parse errors
  }
  return null;
};

export default function AtRiskMapDrawer({ open, onClose, move }: AtRiskMapDrawerProps) {
  const { isLoaded } = useJsApiLoader(getGoogleMapsLoaderOptions());
  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapReady, setMapReady] = useState(false);
  const [mapType, setMapType] = useState<'roadmap' | 'hybrid'>('roadmap');

  // Refs for markers and overlays
  const driverMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const originMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const destMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null);
  const originCircleRef = useRef<google.maps.Circle | null>(null);
  const destCircleRef = useRef<google.maps.Circle | null>(null);
  const routeRendererRef = useRef<google.maps.DirectionsRenderer | null>(null);

  const onMapLoad = useCallback((map: google.maps.Map) => {
    mapRef.current = map;
    setMapReady(true);
  }, []);

  // Update map type when toggled
  useEffect(() => {
    if (!mapRef.current) return;
    mapRef.current.setMapTypeId(mapType);
  }, [mapType]);

  // Clear all markers and overlays
  const clearMapElements = useCallback(() => {
    if (driverMarkerRef.current) {
      driverMarkerRef.current.map = null;
      driverMarkerRef.current = null;
    }
    if (originMarkerRef.current) {
      originMarkerRef.current.map = null;
      originMarkerRef.current = null;
    }
    if (destMarkerRef.current) {
      destMarkerRef.current.map = null;
      destMarkerRef.current = null;
    }
    if (originCircleRef.current) {
      originCircleRef.current.setMap(null);
      originCircleRef.current = null;
    }
    if (destCircleRef.current) {
      destCircleRef.current.setMap(null);
      destCircleRef.current = null;
    }
    if (routeRendererRef.current) {
      routeRendererRef.current.setMap(null);
      routeRendererRef.current = null;
    }
  }, []);

  // Update map when move changes
  useEffect(() => {
    if (!open || !mapReady || !mapRef.current || !move) {
      return;
    }

    clearMapElements();

    const originCoords = parseCoordinates(move.origin_coordinates);
    const destCoords = parseCoordinates(move.dest_coordinates);
    const driverCoords = move.driver_lat && move.driver_lng
      ? { lat: move.driver_lat, lng: move.driver_lng }
      : null;

    // Create bounds to fit all points
    const bounds = new google.maps.LatLngBounds();

    // Add driver marker with truck icon
    if (driverCoords && window.google?.maps?.marker?.AdvancedMarkerElement) {
      bounds.extend(driverCoords);

      const truckIcon = createTruckMarkerIcon({
        speed: move.driver_speed || 0,
        heading: move.driver_heading || undefined,
        hasDispatch: true,
        isCompleted: false,
        remainingDispatches: 0,
        hasOriginArrivalTime: move.status === 'At Origin' || move.status === 'In Transit' || move.status === 'At Destination',
        truckType: 'TruckWithContainer',
        sizeMultiplier: 1.3,
        unitNumber: move.driver,
        isSelected: true,
        isDarkMode: false,
        opacity: 1,
        isOOS: false,
      });

      driverMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: driverCoords,
        map: mapRef.current,
        content: truckIcon,
        title: `Driver ${move.driver}`,
        zIndex: 1000,
      });
    }

    // Add origin marker with geofence circle
    if (originCoords && window.google?.maps?.marker?.PinElement) {
      bounds.extend(originCoords);

      const originPin = new google.maps.marker.PinElement({
        background: '#9333EA', // Purple for origin
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
        scale: 1.0,
      });

      originMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: originCoords,
        map: mapRef.current,
        content: originPin.element,
        title: `Origin: ${move.origin_name}`,
        zIndex: 900,
      });

      // Origin geofence circle
      const originRadius = move.origin_geofence_radius > 0 ? move.origin_geofence_radius : 402; // Default 0.25 miles
      originCircleRef.current = new google.maps.Circle({
        strokeColor: '#9333EA',
        strokeOpacity: 0.6,
        strokeWeight: 2,
        fillColor: '#9333EA',
        fillOpacity: 0.1,
        map: mapRef.current,
        center: originCoords,
        radius: originRadius,
      });
    }

    // Add destination marker with geofence circle
    if (destCoords && window.google?.maps?.marker?.PinElement) {
      bounds.extend(destCoords);

      const destPin = new google.maps.marker.PinElement({
        background: '#DC2626', // Red for destination
        borderColor: '#ffffff',
        glyphColor: '#ffffff',
        scale: 1.0,
      });

      destMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
        position: destCoords,
        map: mapRef.current,
        content: destPin.element,
        title: `Destination: ${move.dest_name}`,
        zIndex: 900,
      });

      // Destination geofence circle
      const destRadius = move.dest_geofence_radius > 0 ? move.dest_geofence_radius : 402; // Default 0.25 miles
      destCircleRef.current = new google.maps.Circle({
        strokeColor: '#DC2626',
        strokeOpacity: 0.6,
        strokeWeight: 2,
        fillColor: '#DC2626',
        fillOpacity: 0.1,
        map: mapRef.current,
        center: destCoords,
        radius: destRadius,
      });
    }

    // Draw route between origin and destination
    if (originCoords && destCoords) {
      const directionsService = new google.maps.DirectionsService();

      directionsService.route(
        {
          origin: originCoords,
          destination: destCoords,
          travelMode: google.maps.TravelMode.DRIVING,
        },
        (result, status) => {
          if (status === google.maps.DirectionsStatus.OK && result) {
            routeRendererRef.current = new google.maps.DirectionsRenderer({
              map: mapRef.current,
              suppressMarkers: true,
              preserveViewport: true, // Don't re-fit the map when the route arrives — fitBounds below already framed driver + origin + dest

              polylineOptions: {
                strokeColor: '#9333EA', // Purple route
                strokeWeight: 3,
                strokeOpacity: 0.8,
              },
            });
            routeRendererRef.current.setDirections(result);
          }
        }
      );
    }

    // Fit map to bounds
    if (!bounds.isEmpty()) {
      mapRef.current.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }

  }, [open, mapReady, move, mapType, clearMapElements]);

  // Cleanup on close
  useEffect(() => {
    if (!open) {
      clearMapElements();
      setMapReady(false);
    }
  }, [open, clearMapElements]);

  if (!open) return null;

  const getRiskBadgeClass = (riskLevel: string) => {
    switch (riskLevel) {
      case 'critical': return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400';
      case 'high': return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400';
      case 'medium': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400';
      case 'low': return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400';
      case 'arrived': return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400';
      default: return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400';
    }
  };

  return (
    <div className="fixed top-[65px] bottom-[48px] left-0 right-0 z-50 overflow-hidden">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-scrim/30 transition-opacity"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="absolute inset-y-0 right-0 w-1/2 flex">
        <div className="w-full bg-panel shadow-xl flex flex-col">
          {/* Header */}
          <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-heading">
                {move?.pronumber}-{move?.legnum}
              </h2>
              <p className="text-sm text-muted">
                Driver: {move?.driver}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          {/* Info Panel */}
          {move && (
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-muted text-xs uppercase mb-1">Origin</div>
                  <div className="font-medium text-heading">{move.origin_name}</div>
                  <div className="text-muted text-xs">{move.origin_city_state}</div>
                </div>
                <div>
                  <div className="text-muted text-xs uppercase mb-1">Destination</div>
                  <div className="font-medium text-heading">{move.dest_name}</div>
                  <div className="text-muted text-xs">{move.dest_city_state}</div>
                </div>
                <div>
                  <div className="text-muted text-xs uppercase mb-1">Deadline</div>
                  <div className="font-medium text-heading">{move.deadline_time}</div>
                  {move.appointment_time && (
                    <div className="text-muted text-xs">Appt: {move.appointment_time}</div>
                  )}
                </div>
                <div>
                  <div className="text-muted text-xs uppercase mb-1">Status</div>
                  <div className="flex items-center gap-2">
                    <span className={`inline-flex px-2 py-0.5 rounded text-xs font-medium ${getRiskBadgeClass(move.risk_level)}`}>
                      {move.risk_level.charAt(0).toUpperCase() + move.risk_level.slice(1)}
                    </span>
                    <span className="text-gray-600 dark:text-gray-300">{move.status}</span>
                  </div>
                  {move.distance_to_dest_miles !== null && (
                    <div className="text-muted text-xs mt-1">
                      {move.distance_to_dest_miles} miles to destination
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Map */}
          <div className="flex-1 relative">
            {isLoaded ? (
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                center={DEFAULT_CENTER}
                zoom={10}
                onLoad={onMapLoad}
                options={{
                  mapId: '3cf64d0fcebdaf11f3625d6e',
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: true,
                  zoomControl: true,
                  clickableIcons: false,
                  gestureHandling: 'greedy',
                }}
              />
            ) : (
              <div className="flex items-center justify-center h-full bg-gray-100 dark:bg-gray-900">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
              </div>
            )}
            {/* Satellite Toggle Button */}
            {isLoaded && (
              <button
                onClick={() => setMapType(prev => prev === 'roadmap' ? 'hybrid' : 'roadmap')}
                className="absolute top-3 left-3 bg-white hover:bg-gray-100 dark:bg-gray-700 dark:hover:bg-gray-600 rounded shadow-md p-2 z-10"
                title={mapType === 'roadmap' ? 'Switch to Satellite view' : 'Switch to Map view'}
              >
                {mapType === 'roadmap' ? (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-600 dark:text-gray-300">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M2 12h20" />
                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-6 h-6 text-gray-600 dark:text-gray-300">
                    <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" />
                    <path d="M9 3v15" />
                    <path d="M15 6v15" />
                  </svg>
                )}
              </button>
            )}
          </div>

          {/* Legend */}
          <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900/50">
            <div className="flex items-center justify-center gap-6 text-xs">
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-purple-500" />
                <span className="text-gray-600 dark:text-gray-400">Origin</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded-full bg-red-500" />
                <span className="text-gray-600 dark:text-gray-400">Destination</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-3 h-3 rounded bg-blue-500" />
                <span className="text-gray-600 dark:text-gray-400">Driver</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-6 h-0.5 bg-purple-500" />
                <span className="text-gray-600 dark:text-gray-400">Route</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
